import { useState, useRef, useEffect } from "react";
import Input from "../../UI/Input";
import "./DashboardDateFilter.css";

interface DashboardSingleDateFilterProps {
    id: string;
    label: string;
    value: string;
    onChange: (value: string) => void;
    disabled?: boolean;
}

const todayISO = () => new Date().toISOString().split('T')[0];

const DashboardSingleDateFilter = ({ id, label, value, onChange, disabled = false }: DashboardSingleDateFilterProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
                setIsOpen(false);
            }
        };
        if (isOpen) document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [isOpen]);

    useEffect(() => {
        if (disabled) {
            setIsOpen(false);
        }
    }, [disabled]);

    const handleTodayClick = () => {
        if (disabled) return;
        const today = todayISO();
        onChange(value === today ? "" : today);
    };

    const handleClear = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (disabled) return;
        onChange("");
    };

    const hasValue = value !== "";
    const isToday = value === todayISO();

    return (
        <div className="dashboard-date-filter" ref={containerRef}>
            <label className="dashboard-date-filter-label">{label}</label>

            <div
                className={`dashboard-date-filter-trigger${isOpen ? " open" : ""}${hasValue ? " has-value" : ""}${disabled ? " disabled" : ""}`}
                onClick={() => { if (!disabled) setIsOpen(prev => !prev); }}
                role="button"
                tabIndex={disabled ? -1 : 0}
                aria-disabled={disabled}
                onKeyDown={e => { if (!disabled && (e.key === "Enter" || e.key === " ")) setIsOpen(prev => !prev); }}
                id={id}
            >
                <span className={`dashboard-date-filter-display${!hasValue ? " placeholder" : ""}`}>
                    {value || "Select date"}
                </span>
                <div className="dashboard-date-filter-trigger-actions">
                    {hasValue && (
                        <span
                            className="ri-close-line dashboard-date-filter-clear"
                            onClick={handleClear}
                            title="Clear"
                        />
                    )}
                    {!hasValue && <span className="ri-calendar-line dashboard-date-filter-arrow" />}
                    {hasValue && <span className={`ri-arrow-${isOpen ? "up" : "down"}-s-line dashboard-date-filter-arrow`} />}
                </div>
            </div>

            {isOpen && (
                <div className="dashboard-date-filter-popover">
                    <p className="dashboard-date-filter-section-label">Quick select</p>
                    <div className="dashboard-date-filter-chips">
                        <button
                            type="button"
                            className={`dashboard-date-filter-chip${isToday ? " active" : ""}`}
                            onClick={handleTodayClick}
                            title="Current day"
                        >
                            Today
                        </button>
                    </div>

                    <div className="dashboard-date-filter-divider">
                        <span>or pick a date</span>
                    </div>

                    <Input
                        id={`${id}-input`}
                        label="Date"
                        type="date"
                        value={value}
                        onChange={onChange}
                        disabled={disabled}
                    />
                </div>
            )}
        </div>
    );
};

export default DashboardSingleDateFilter;
